import {
  CaptureUpdateAction,
  convertToExcalidrawElements,
  getVisibleSceneBounds,
} from "@excalidraw/excalidraw";
import { newElementWith } from "@excalidraw/element";

import type { ExcalidrawElementSkeleton } from "@excalidraw/element";
import type {
  ExcalidrawElement,
  NonDeletedExcalidrawElement,
} from "@excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

export type Point = { x: number; y: number };

export type AgentOperations = {
  reply: string;
  add: Record<string, any>[];
  update: ({ id: string } & Record<string, any>)[];
  remove: string[];
};

/** max elements sent to the agent as board context (nearest to the click) */
const MAX_CONTEXT_ELEMENTS = 400;

const ADDABLE_TYPES = new Set([
  "rectangle",
  "ellipse",
  "diamond",
  "text",
  "arrow",
  "line",
  "stickynote",
]);

const UPDATABLE_PROPS = [
  "x",
  "y",
  "width",
  "height",
  "angle",
  "strokeColor",
  "backgroundColor",
  "fillStyle",
  "strokeWidth",
  "strokeStyle",
  "roughness",
  "opacity",
] as const;

const round = (value: number) => Math.round(value);

// -----------------------------------------------------------------------------
// board context for the agent (coordinates relative to the click point)
// -----------------------------------------------------------------------------

const compactElement = (
  element: NonDeletedExcalidrawElement,
  point: Point,
  boundTexts: Map<string, string>,
) => {
  const compact: Record<string, unknown> = {
    id: element.id,
    type: element.type,
    x: round(element.x - point.x),
    y: round(element.y - point.y),
    width: round(element.width),
    height: round(element.height),
  };
  if (element.angle) {
    compact.angle = element.angle;
  }
  if (element.strokeColor !== "#1e1e1e") {
    compact.strokeColor = element.strokeColor;
  }
  if (element.backgroundColor !== "transparent") {
    compact.backgroundColor = element.backgroundColor;
  }
  if (element.type === "text") {
    compact.text = element.text;
    compact.fontSize = element.fontSize;
  }
  const label = boundTexts.get(element.id);
  if (label) {
    compact.label = label;
  }
  if (element.type === "arrow" || element.type === "line") {
    compact.points = element.points.map(([px, py]) => [round(px), round(py)]);
    if (element.type === "arrow") {
      compact.startBoundTo = element.startBinding?.elementId ?? null;
      compact.endBoundTo = element.endBinding?.elementId ?? null;
    }
  }
  if (element.frameId) {
    compact.frameId = element.frameId;
  }
  return compact;
};

export const getBoardContext = (api: ExcalidrawImperativeAPI, point: Point) => {
  const elements = api.getSceneElements();
  const appState = api.getAppState();

  // container labels are separate text elements; fold them into the container
  const boundTexts = new Map<string, string>();
  for (const element of elements) {
    if (element.type === "text" && element.containerId) {
      boundTexts.set(element.containerId, element.text);
    }
  }

  const distance = (element: NonDeletedExcalidrawElement) =>
    Math.hypot(
      element.x + element.width / 2 - point.x,
      element.y + element.height / 2 - point.y,
    );

  const relevant = elements
    .filter((element) => !(element.type === "text" && element.containerId))
    .filter((element) => element.type !== "selection")
    .sort((a, b) => distance(a) - distance(b))
    .slice(0, MAX_CONTEXT_ELEMENTS)
    .map((element) => compactElement(element, point, boundTexts));

  const [minX, minY, maxX, maxY] = getVisibleSceneBounds(appState);

  return {
    elements: relevant,
    viewport: {
      x: round(minX - point.x),
      y: round(minY - point.y),
      width: round(maxX - minX),
      height: round(maxY - minY),
    },
    selectedIds: Object.keys(appState.selectedElementIds).filter(
      (id) => appState.selectedElementIds[id],
    ),
  };
};

// -----------------------------------------------------------------------------
// applying the agent's operations
// -----------------------------------------------------------------------------

const offsetSkeleton = (skeleton: Record<string, any>, point: Point) => ({
  ...skeleton,
  x: (Number(skeleton.x) || 0) + point.x,
  y: (Number(skeleton.y) || 0) + point.y,
});

export const applyAgentOperations = (
  api: ExcalidrawImperativeAPI,
  ops: AgentOperations,
  point: Point,
) => {
  const skeletons = ops.add
    .filter((skeleton) => ADDABLE_TYPES.has(skeleton?.type))
    .map((skeleton) => offsetSkeleton(skeleton, point));

  const added = convertToExcalidrawElements(
    skeletons as ExcalidrawElementSkeleton[],
    { regenerateIds: true },
  );

  const updates = new Map(ops.update.map((update) => [update.id, update]));
  const removed = new Set(ops.remove);

  const existing = api.getSceneElementsIncludingDeleted();
  // deleting a container also deletes its label
  for (const element of existing) {
    if (
      element.type === "text" &&
      element.containerId &&
      removed.has(element.containerId)
    ) {
      removed.add(element.id);
    }
  }

  let changed = 0;
  const nextElements: ExcalidrawElement[] = existing.map((element) => {
    if (element.isDeleted) {
      return element;
    }
    if (removed.has(element.id)) {
      changed++;
      return newElementWith(element, { isDeleted: true });
    }
    const update = updates.get(element.id);
    if (!update) {
      return element;
    }
    const props: Record<string, unknown> = {};
    for (const key of UPDATABLE_PROPS) {
      if (update[key] !== undefined && update[key] !== null) {
        props[key] = update[key];
      }
    }
    if (typeof props.x === "number") {
      props.x = (props.x as number) + point.x;
    }
    if (typeof props.y === "number") {
      props.y = (props.y as number) + point.y;
    }
    if (!Object.keys(props).length) {
      return element;
    }
    changed++;
    return newElementWith(element, props as any);
  });

  if (!added.length && !changed) {
    return { added: 0, changed: 0 };
  }

  api.updateScene({
    elements: [...nextElements, ...added],
    appState: added.length
      ? {
          selectedElementIds: Object.fromEntries(
            added
              .filter(
                (element) => !("containerId" in element && element.containerId),
              )
              .map((element) => [element.id, true as const]),
          ),
        }
      : undefined,
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });

  return { added: added.length, changed };
};
